import { useCallback, useEffect, useRef, useState } from 'react';

import {
  CreateLaneBooking,
  CreateLaneBookingRequest,
  GetLaneBooking,
  LaneBooking,
  laneErrorMessage,
  unwrapLanePayload,
} from '../../../apis/lanePlanner';
import { usePollUntil } from './usePollUntil';

export const BOOKING_POLL_MS = 5000;
const BOOKING_POLL_LIMIT = 24;
/** Vendor words after which the booking will not change again. */
const FINAL_VENDOR_STATUSES = new Set([
  'CONFIRMED',
  'REJECTED',
  'FAILED',
  'CANCELLED',
]);

/** The vendor answered but will not honour the booking. */
export const isRefused = (booking: LaneBooking | null): boolean =>
  !!booking &&
  ['REJECTED', 'FAILED', 'CANCELLED'].includes(
    (booking.vendor_status ?? '').toUpperCase()
  );

/** True while the vendor has accepted the booking but not yet confirmed it. */
export const isConfirming = (booking: LaneBooking | null): boolean =>
  !!booking &&
  booking.status === 'created' &&
  !FINAL_VENDOR_STATUSES.has((booking.vendor_status ?? '').toUpperCase());

export interface LaneBookingState {
  booking: LaneBooking | null;
  /** Polling gave up while the vendor still had not confirmed. */
  stalled: boolean;
  submitting: boolean;
  /** Why the last attempt did not book: a refusal, or a call that failed. */
  error: string | null;
  submit: (request: CreateLaneBookingRequest) => Promise<void>;
  reset: () => void;
}

/**
 * Books one quote. The create call is sent at most once at a time, and a
 * booking the vendor has accepted but not confirmed is polled until it is.
 * A refusal comes back as a normal answer with `status: 'failed'`, so it is
 * read from the booking, not from the HTTP status.
 */
export const useLaneBooking = (token: string): LaneBookingState => {
  const [booking, setBooking] = useState<LaneBooking | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stalled, setStalled] = useState(false);
  const generation = useRef(0);
  const sending = useRef(false);

  const reset = useCallback((): void => {
    generation.current += 1;
    sending.current = false;
    setBooking(null);
    setStalled(false);
    setSubmitting(false);
    setError(null);
  }, []);

  useEffect(() => reset, [reset]);

  const submit = useCallback(
    async (request: CreateLaneBookingRequest): Promise<void> => {
      if (sending.current) return;
      sending.current = true;
      const mine = ++generation.current;
      setSubmitting(true);
      setError(null);
      try {
        const res = await CreateLaneBooking(token, request);
        if (mine !== generation.current) return;
        const created = unwrapLanePayload<LaneBooking>(res);
        if (created.status === 'failed') {
          setError(
            created.error_message ?? 'The vendor did not accept the booking.'
          );
        } else {
          setBooking(created);
        }
      } catch (err) {
        if (mine !== generation.current) return;
        setError(laneErrorMessage(err, 'Could not book. Please try again.'));
      } finally {
        if (mine === generation.current) {
          sending.current = false;
          setSubmitting(false);
        }
      }
    },
    [token]
  );

  const bookingId = booking?.id;
  const confirming = isConfirming(booking);
  const poller = usePollUntil();
  useEffect(() => {
    if (!bookingId || !confirming) return undefined;
    const mine = generation.current;
    poller.start({
      firstDelayMs: BOOKING_POLL_MS,
      maxPolls: BOOKING_POLL_LIMIT,
      retryOnAnyError: true, // keep what we have; the next poll tries again
      fetch: () => GetLaneBooking(token, bookingId),
      onResult: (res) => {
        if (mine !== generation.current) return { done: true };
        const latest = unwrapLanePayload<LaneBooking>(res);
        setBooking(latest);
        return { done: !isConfirming(latest), delayMs: BOOKING_POLL_MS };
      },
      onTimeout: () => {
        if (mine === generation.current) setStalled(true);
      },
    });
    return () => poller.stop();
  }, [token, bookingId, confirming, poller]);

  return { booking, stalled, submitting, error, submit, reset };
};
